import React, { useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';

import { scrollBoxStyles, MAX_FILE_SIZE, DEFAULT_COLORS } from './ButtonContent.constants';
import { ContentType, StateType, ButtonContentProps } from './ButtonContent';
import { getStateBasedProp, updateStateBasedProp } from './ButtonContent.utils';

import StateSelector from './StateSelector';
import HoverToggleSwitch from './HoverToggleSwitch';
import ContentChips from './ContentChips';
import TextControls from './TextControls';
import IconControls from './IconControls';
import ImageControls from './ImageControls';

const ButtonContent: React.FC<ButtonContentProps> = ({
  selectedImage,
  updateImage,
  iconColor,
  backgroundColor,
  selectedTabBackgroundColor,
}) => {
  const [textColorPickerOpen, setTextColorPickerOpen] = useState(false);
  const [iconColorPickerOpen, setIconColorPickerOpen] = useState(false);
  const [showAllHoverProps, setShowAllHoverProps] = useState(false);
  const [activeContent, setActiveContent] = useState<ContentType | null>(null);
  const [activeState, setActiveState] = useState<StateType>('default');
  const [imageName, setImageName] = useState('');

  const {
    contentOrder = [],
    text = '',
    imageUrl = '',
    textProps = {},
    iconProps = {},
    imageProps = {},
    hoverEnabled = true,
  } = selectedImage;

  const currentContentOrder = getStateBasedProp(contentOrder, activeState) || [];
  const currentTextProps = getStateBasedProp(textProps, activeState) || {};
  const currentIconProps = getStateBasedProp(iconProps, activeState) || {};
  const currentImageProps = getStateBasedProp(imageProps, activeState) || {};

  useEffect(() => {
    if (activeState === 'hover') {
      ensureHoverDefaults();
    }
  }, [activeState]);

  const ensureHoverDefaults = () => {
    const defaultOrder = getStateBasedProp(contentOrder, 'default') || [];

    if (defaultOrder.length > 0 && !getStateBasedProp(contentOrder, 'hover')) {
      updateImage({
        contentOrder: updateStateBasedProp(contentOrder, defaultOrder, 'hover'),
      });
    }

    if (defaultOrder.includes('text') && !getStateBasedProp(textProps, 'hover')?.color) {
      updateImage({
        textProps: updateStateBasedProp(textProps, { color: DEFAULT_COLORS.textHover }, 'hover'),
      });
    }

    if (defaultOrder.includes('icon') && !getStateBasedProp(iconProps, 'hover')?.color) {
      updateImage({
        iconProps: updateStateBasedProp(iconProps, { color: DEFAULT_COLORS.iconHover }, 'hover'),
      });
    }

    if (defaultOrder.includes('image') && !getStateBasedProp(imageProps, 'hover')?.opacity) {
      updateImage({
        imageProps: updateStateBasedProp(
          imageProps,
          { opacity: DEFAULT_COLORS.opacityHover },
          'hover',
        ),
      });
    }
  };

  useEffect(() => {
    if (!activeContent) {
      setActiveContent(
        currentContentOrder.includes('text') ? 'text' : currentContentOrder[0] || null,
      );
    }
  }, [currentContentOrder, activeContent]);

  //image upload handler

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setImageName(file.name);
      const reader = new FileReader();
      reader.onload = (ev) => {
        const result = ev.target?.result;
        if (typeof result === 'string') {
          updateImage({ imageUrl: result });
        }
      };
      reader.readAsDataURL(file);
    },
    [updateImage],
  );

  return (
    <Box sx={scrollBoxStyles}>
      <div className="optionsInfo">
        <div style={{ display: 'flex', paddingBottom: '8px', flexDirection: 'column' }}>
          <StateSelector
            activeState={activeState}
            setActiveState={setActiveState}
            iconColor={iconColor}
            backgroundColor={backgroundColor}
          />

          <HoverToggleSwitch
            backgroundColor={backgroundColor}
            iconColor={iconColor}
            activeState={activeState}
            hoverEnabled={hoverEnabled}
            showAllHoverProps={showAllHoverProps}
            updateImage={updateImage}
            setShowAllHoverProps={setShowAllHoverProps}
          />

          {(activeState === 'default' || showAllHoverProps) && (
            <ContentChips
              currentContentOrder={currentContentOrder}
              activeContent={activeContent}
              setActiveContent={setActiveContent}
              updateImage={updateImage}
              rawContentOrder={contentOrder}
              activeState={activeState}
              color={iconColor}
              backgroundColor={backgroundColor}
              selectedTabBackgroundColor={selectedTabBackgroundColor}
            />
          )}

          {(activeState === 'default' || (showAllHoverProps && hoverEnabled)) && (
            <>
              {activeContent === 'text' && (
                <TextControls
                  text={text}
                  textProps={textProps}
                  currentTextProps={currentTextProps}
                  activeState={activeState}
                  updateImage={updateImage}
                  textColorPickerOpen={textColorPickerOpen}
                  setTextColorPickerOpen={setTextColorPickerOpen}
                  iconColor={iconColor}
                />
              )}

              {activeContent === 'icon' && (
                <IconControls
                  selectedImage={selectedImage}
                  iconProps={iconProps}
                  currentIconProps={currentIconProps}
                  activeState={activeState}
                  updateImage={updateImage}
                  iconColorPickerOpen={iconColorPickerOpen}
                  setIconColorPickerOpen={setIconColorPickerOpen}
                  isMultiContent={currentContentOrder.length > 1}
                  iconColor={iconColor}
                  backgroundColor={backgroundColor}
                />
              )}

              {activeContent === 'image' && (
                <ImageControls
                  imageUrl={imageUrl}
                  imageName={imageName}
                  imageProps={imageProps}
                  currentImageProps={currentImageProps}
                  handleFileUpload={handleFileUpload}
                  updateImage={updateImage}
                  activeState={activeState}
                  isMultiContent={currentContentOrder.length > 1}
                  iconColor={iconColor}
                />
              )}
            </>
          )}
        </div>
      </div>
    </Box>
  );
};

export default ButtonContent;
